import moment from 'moment';

/**
 * Shared formatting helpers for the Queue Monitor v1 components.
 *
 * Conventions (locked by the v1 contract):
 * - null/undefined rates and counts render as an em dash, never 0.
 * - Legacy per-request count fields are capped at COUNT_CAP + 1 = 25001 and render "25,000+".
 * - New instant fields are UTC ISO-8601 ("...Z"); UTC clock times render as HH:mm,
 *   Eastern Time renderings use the browser-native Intl API with the America/New_York
 *   zone (moment alone cannot convert to a named timezone and moment-timezone is not a
 *   dependency; no new npm deps allowed).
 */

export const EM_DASH = '—';

export const COUNT_CAP = 25000;

const ET_TIME_ZONE = 'America/New_York';

// Newer ICU versions emit a narrow no-break space before AM/PM; normalize to a
// plain space so rendered text is stable and assertable.
const normalizeSpaces = (text) => text.replace(/[\u202F\u00A0]/g, ' ');

const toValidDate = (iso) => {
	if (!iso) {
		return null;
	}
	const date = new Date(iso);
	return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Thousands-separated integer/float, em dash for null/undefined.
 */
export const formatNumber = (value) => {
	if (value === null || value === undefined) {
		return EM_DASH;
	}
	if (typeof value === 'number' && Number.isFinite(value)) {
		return value.toLocaleString('en-US');
	}
	return String(value);
};

/**
 * Rate with at most 1 decimal (contract: rates are floats rounded to 1 decimal),
 * em dash for null/undefined.
 */
export const formatRate = (value) => {
	if (value === null || value === undefined || !Number.isFinite(value)) {
		return EM_DASH;
	}
	return value.toLocaleString('en-US', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 1,
	});
};

/**
 * Legacy capped count fields: COUNT_CAP + 1 means "at least the cap" and renders "25,000+".
 */
export const formatCapAware = (value) => {
	if (value === null || value === undefined) {
		return EM_DASH;
	}
	if (value === COUNT_CAP + 1) {
		return `${COUNT_CAP.toLocaleString('en-US')}+`;
	}
	return formatNumber(value);
};

/**
 * UTC wall-clock "HH:mm" from a UTC ISO instant (e.g. "2026-06-12T15:54:00Z" -> "15:54").
 */
export const formatUtcHHmm = (iso) => {
	if (!iso) {
		return EM_DASH;
	}
	// Strict ISO-8601 parsing: avoids moment's js-Date fallback (and its
	// deprecation warning) on malformed input.
	const parsed = moment.utc(iso, moment.ISO_8601, true);
	return parsed.isValid() ? parsed.format('HH:mm') : EM_DASH;
};

/**
 * Eastern Time clock time, e.g. "12:00 PM" (caller appends " ET").
 */
export const formatEtTime = (iso) => {
	const date = toValidDate(iso);
	if (!date) {
		return EM_DASH;
	}
	return normalizeSpaces(
		new Intl.DateTimeFormat('en-US', {
			timeZone: ET_TIME_ZONE,
			hour: 'numeric',
			minute: '2-digit',
		}).format(date),
	);
};

/**
 * Eastern Time date + time, e.g. "Jun 12, 12:00 PM" (caller appends " ET").
 */
export const formatEtDateTime = (iso) => {
	const date = toValidDate(iso);
	if (!date) {
		return EM_DASH;
	}
	return normalizeSpaces(
		new Intl.DateTimeFormat('en-US', {
			timeZone: ET_TIME_ZONE,
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		}).format(date),
	);
};

/**
 * Whole minutes elapsed since a UTC ISO instant; null for missing/invalid input.
 * Clamped at zero so a slightly-future server timestamp cannot render "-1 min ago".
 */
export const minutesSince = (iso, now = new Date()) => {
	const date = toValidDate(iso);
	if (!date) {
		return null;
	}
	return Math.max(0, Math.floor((now.getTime() - date.getTime()) / 60000));
};
